const PUBLIC_SITE_ORIGIN = 'https://www.muzology.com';
const PARENT_LANDING_PATH = '/parents';
const PARENT_CHECKOUT_PATH = '/checkout';
const TEACHER_SIGNUP_PATH = '/signup';

const ROUTE_POLICY = {
    [PARENT_LANDING_PATH]: {
        kind: 'landing',
        indexable: true,
        includeInSitemap: true,
        canonicalTarget: PARENT_LANDING_PATH,
    },
    '/learn-more': {
        kind: 'info',
        indexable: true,
        includeInSitemap: true,
        canonicalTarget: '/learn-more',
    },
    [TEACHER_SIGNUP_PATH]: {
        kind: 'teacher',
        indexable: true,
        includeInSitemap: true,
        canonicalTarget: TEACHER_SIGNUP_PATH,
    },
    [PARENT_CHECKOUT_PATH]: {
        kind: 'checkout',
        indexable: false,
        includeInSitemap: false,
        canonicalTarget: PARENT_CHECKOUT_PATH,
        noindex: true,
    },
    '/offer': {
        kind: 'redirect',
        indexable: false,
        includeInSitemap: false,
        canonicalTarget: PARENT_LANDING_PATH,
        redirectTo: PARENT_LANDING_PATH,
        noindex: true,
    },
    '/offers': {
        kind: 'redirect',
        indexable: false,
        includeInSitemap: false,
        canonicalTarget: PARENT_LANDING_PATH,
        redirectTo: PARENT_LANDING_PATH,
        noindex: true,
    },
    '/parent-checkout': {
        kind: 'redirect',
        indexable: false,
        includeInSitemap: false,
        canonicalTarget: PARENT_CHECKOUT_PATH,
        redirectTo: PARENT_CHECKOUT_PATH,
        noindex: true,
    },
    '/holiday-gift': {
        kind: 'campaign',
        indexable: false,
        includeInSitemap: false,
        canonicalTarget: '/holiday-gift',
        noindex: true,
    },
    '/summer-slide': {
        kind: 'campaign',
        indexable: false,
        includeInSitemap: false,
        canonicalTarget: '/summer-slide',
        noindex: true,
    },
    '/summer-slide-checkout': {
        kind: 'campaign-checkout',
        indexable: false,
        includeInSitemap: false,
        canonicalTarget: '/summer-slide-checkout',
        noindex: true,
    },
};

const NON_PRODUCTION_HOSTS = new Set([
    'web-dev.muzology.com',
    'web-staging.muzology.com',
]);

function normalizeHostname(hostname) {
    return (hostname || '').split(':')[0].trim().toLowerCase();
}

function normalizePathname(pathname) {
    if (!pathname) {
        return '/';
    }

    const basePath = pathname.split('?')[0].split('#')[0] || '/';
    if (basePath !== '/' && basePath.endsWith('/')) {
        return basePath.slice(0, -1);
    }

    return basePath;
}

function getRoutePolicy(pathname) {
    const normalizedPathname = normalizePathname(pathname);
    return ROUTE_POLICY[normalizedPathname] || {
        kind: 'page',
        indexable: true,
        includeInSitemap: true,
        canonicalTarget: normalizedPathname,
    };
}

function isNonProductionHost(hostname) {
    const normalizedHostname = normalizeHostname(hostname);
    if (!normalizedHostname) {
        return false;
    }

    if (NON_PRODUCTION_HOSTS.has(normalizedHostname)) {
        return true;
    }

    if (normalizedHostname.endsWith('.vercel.app') || normalizedHostname.endsWith('.vercel.sh')) {
        return true;
    }

    return false;
}

function shouldNoindexRequest(hostname, pathname) {
    if (isNonProductionHost(hostname)) {
        return true;
    }

    const routePolicy = getRoutePolicy(pathname);
    return routePolicy.indexable === false || routePolicy.noindex === true;
}

function getCanonicalUrl(origin, pathname) {
    const routePolicy = getRoutePolicy(pathname);
    return new URL(routePolicy.canonicalTarget, ensureOrigin(origin)).toString();
}

function buildPublicSeoProps(origin, pathname, seo = {}) {
    const routePolicy = getRoutePolicy(pathname);
    const canonical = getCanonicalUrl(origin, pathname);
    const image = seo?.image?.url
        ? [
            {
                url: seo.image.url,
                width: seo.image.width,
                height: seo.image.height,
            },
        ]
        : undefined;

    const openGraph = {
        ...(seo?.openGraph || {}),
        url: canonical,
    };

    if (!openGraph.images && image) {
        openGraph.images = image;
    }

    return {
        ...seo,
        canonical,
        noindex: routePolicy.indexable === false || routePolicy.noindex === true,
        nofollow: routePolicy.indexable === false || routePolicy.noindex === true,
        openGraph,
        twitter: seo?.twitterCard
            ? {
                cardType: seo.twitterCard,
            }
            : seo?.twitter,
    };
}

function getRedirectRules() {
    return Object.entries(ROUTE_POLICY)
        .filter(([, routePolicy]) => routePolicy.redirectTo)
        .map(([source, routePolicy]) => ({
            source,
            destination: routePolicy.redirectTo,
            permanent: true,
        }));
}

function ensureOrigin(origin) {
    const safeOrigin = origin || PUBLIC_SITE_ORIGIN;
    return safeOrigin.endsWith('/') ? safeOrigin : `${safeOrigin}/`;
}

module.exports = {
    PUBLIC_SITE_ORIGIN,
    PARENT_LANDING_PATH,
    PARENT_CHECKOUT_PATH,
    TEACHER_SIGNUP_PATH,
    ROUTE_POLICY,
    getRoutePolicy,
    getRedirectRules,
    isNonProductionHost,
    shouldNoindexRequest,
    getCanonicalUrl,
    buildPublicSeoProps,
};
